/**
 * Shared hook that creates and memoizes a Graph API client instance.
 *
 * Resolves site ID and list IDs on first use (lazy initialization).
 * All list-specific hooks depend on this hook for client + ID resolution.
 *
 * 13-08 (ISS-185): the client is memoised per sign-in over the stable MSAL instance (no ref read
 * during render), and the resolved ids are stored WITH the client they belong to, so readiness is
 * derived rather than set synchronously inside the effect. Same results as before: one client per
 * sign-in, ids resolved once per client, an unprovisioned list resolves to ''.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Client } from '@microsoft/microsoft-graph-client';
import { useMsal } from '@azure/msal-react';
import { acquireGraphToken, useAuth } from '../auth/useAuth';
import { createGraphClient, getSiteId, getListId } from '../services/graphClient';
import { LIST_NAMES } from '../config/sharepoint';
import type { ListNameKey } from '../config/sharepoint';

/** Resolved list IDs keyed by list name */
export type ListIds = Record<ListNameKey, string>;

export interface UseGraphClientResult {
  client: Client | null;
  siteId: string | null;
  listIds: ListIds | null;
  isReady: boolean;
  error: string | null;
}

interface Resolved {
  client: Client;
  siteId: string | null;
  listIds: ListIds | null;
  isReady: boolean;
  error: string | null;
}

export function useGraphClient(): UseGraphClientResult {
  const { isAuthenticated } = useAuth();
  const { instance } = useMsal();

  // Create the Graph client once per sign-in. The token is read from MSAL when a request needs it.
  const client = useMemo(
    () => (isAuthenticated ? createGraphClient(() => acquireGraphToken(instance)) : null),
    [isAuthenticated, instance],
  );

  const [resolved, setResolved] = useState<Resolved | null>(null);

  const resolveIds = useCallback(async (c: Client) => {
    const resolvedSiteId = await getSiteId(c);

    const listEntries = Object.entries(LIST_NAMES) as Array<
      [ListNameKey, string]
    >;

    const resolvedListIds = {} as Record<ListNameKey, string>;
    await Promise.all(
      listEntries.map(async ([key, displayName]) => {
        try {
          resolvedListIds[key] = await getListId(
            c,
            resolvedSiteId,
            displayName,
          );
        } catch (err) {
          // List not yet provisioned — leave ID empty, hooks will skip
          console.warn(`[useGraphClient] Failed to resolve list "${displayName}":`, err);
          resolvedListIds[key] = '';
        }
      }),
    );

    return { resolvedSiteId, resolvedListIds };
  }, []);

  useEffect(() => {
    if (!client) return;

    let cancelled = false;

    resolveIds(client)
      .then(({ resolvedSiteId, resolvedListIds }) => {
        if (cancelled) return;
        setResolved({
          client,
          siteId: resolvedSiteId,
          listIds: resolvedListIds as ListIds,
          isReady: true,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to resolve SharePoint IDs';
        setResolved((prev) => ({
          client,
          siteId: prev?.client === client ? prev.siteId : null,
          listIds: prev?.client === client ? prev.listIds : null,
          isReady: false,
          error: message,
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [client, resolveIds]);

  // Only results for THIS client count; anything else (signed out, a new sign-in) is not ready.
  const current = client && resolved?.client === client ? resolved : null;

  return {
    client,
    siteId: current?.siteId ?? null,
    listIds: current?.listIds ?? null,
    isReady: current?.isReady ?? false,
    error: current?.error ?? null,
  };
}
